import { ReactNode, useContext, useEffect, useState } from 'react'
import { CopilotUploadPreset } from 'shared/types/copilot'
import { Clipboard } from 'ts-clipboard'
import { PortalMessageContext } from 'ui'
import { CreationToolUploadContext } from '../manager'
import { CopilotContext } from './context'
import CopilotAlertContextManager from './contexts/alert'
import CopilotApiContextManager from './contexts/api'
import CopilotDropdownContextManager from './contexts/dropdown'
import CopilotSettingsContextManager from './contexts/settings'
import CopilotSidebarContextManager from './contexts/sidebar'
import { useHasChanges } from './hooks/use-has-changes'
import { PresetCopiedMessage } from './messages'
import CopilotStorage from './storage'
import { CopilotPresetSettings } from './types'
import {
    channelIdFromPreset,
    isPublicFromPreset,
    mergePresets,
    ratingFromPreset,
    sourceUrlFromPreset,
    tagsFromPreset,
    usernameFromPreset,
} from './utils/preset'
import { getDefaultPresetSettings } from './utils/preset-setting'

type Props = {
    children: ReactNode
    disabled?: boolean
}

const CopilotContextManager = ({ children, disabled = false }: Props) => {
    const { showMessage } = useContext(PortalMessageContext)
    const uploadContext = useContext(CreationToolUploadContext)
    const { resetAllFields, setChannelId, setRating, setSourceUrl, setIsPublic, setTags, setUsername } = uploadContext

    const [activePresetId, setActivePresetId] = useState<string>('')
    const [presets, setPresets] = useState<CopilotUploadPreset[]>([])
    const [settings, setSettings] = useState<CopilotPresetSettings>(getDefaultPresetSettings())
    const hasChanges = useHasChanges(activePresetId ? getPresetById(activePresetId) : null, settings)

    async function activatePreset(id: string) {
        const preset = getPresetById(id)

        if (!preset) return

        if (settings.collections) {
            setChannelId(channelIdFromPreset(preset))
        }

        if (settings.rating) {
            setRating(ratingFromPreset(preset))
        }

        if (settings.sourceUrl) {
            setSourceUrl(sourceUrlFromPreset(preset))
        }

        if (settings.username) {
            setUsername(usernameFromPreset(preset))
        }

        if (settings.tags) {
            setTags(tagsFromPreset(preset))
        }

        if (settings.visibility) {
            setIsPublic(isPublicFromPreset(preset))
        }

        setActivePresetId(id)
    }

    function copyPresetToClipboard(id: string) {
        const preset = getPresetById(id)

        if (preset) {
            Clipboard.copy(preset.id)
            showMessage(<PresetCopiedMessage presetName={preset.name} />)
        }
    }

    function getActivePreset() {
        if (!activePresetId) {
            return null
        }

        return getPresetById(activePresetId)
    }

    function getPresetById(id: string) {
        return presets.find((preset) => preset.id === id) || null
    }

    function removePresetById(id: string) {
        setPresets(presets.filter((p) => p.id !== id))

        if (activePresetId === id) {
            setActivePresetId('')
        }
    }

    function resetPreset() {
        resetAllFields()
        setActivePresetId('')
    }

    function onApiCreate(preset: CopilotUploadPreset) {
        setPresets([...presets, preset])
        setActivePresetId(preset.id)
    }

    function onApiError(message: string) {
        showMessage(message, { style: 'error' })
    }

    function onApiRemove(presetId: string) {
        removePresetById(presetId)
    }

    function onApiUpdate(updated: CopilotUploadPreset[]) {
        setPresets(mergePresets(presets, updated))
    }

    useEffect(() => {
        uploadContext.setCopilotPresetId(activePresetId)
    }, [activePresetId])

    return (
        <CopilotContext.Provider
            value={{
                activePresetId,
                disabled,
                hasChanges,
                presets,
                activatePreset,
                copyPresetToClipboard,
                getActivePreset,
                getPresetById,
                removePresetById,
                resetPreset,
            }}
        >
            <CopilotAlertContextManager>
                <CopilotSettingsContextManager onChange={setSettings}>
                    <CopilotApiContextManager
                        onCreate={onApiCreate}
                        onError={onApiError}
                        onRemove={onApiRemove}
                        onUpdate={onApiUpdate}
                    >
                        <CopilotDropdownContextManager>
                            <CopilotSidebarContextManager>
                                {children}
                                <CopilotStorage presets={presets} />
                            </CopilotSidebarContextManager>
                        </CopilotDropdownContextManager>
                    </CopilotApiContextManager>
                </CopilotSettingsContextManager>
            </CopilotAlertContextManager>
        </CopilotContext.Provider>
    )
}

export default CopilotContextManager
